import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { RotateCcw, Wand2 } from "lucide-react";
import { RevealLines } from "@/components/Reveal";
import { BraceletPreview } from "@/components/BraceletPreview";
import { pickQuestions, CHARACTERS, matchCharacter, BANK_SIZE } from "@/components/tgcfData";
import { CharacterGallery } from "@/components/CharacterGallery";
import { useMakeBracelet } from "@/hooks/useMakeBracelet";
import { useCharm } from "@/hooks/useCharm";
import { TGCF_CHARM } from "@/components/charms";

const Question = ({ questions, step, onPick }) => (
  <motion.div key={`${step}-${questions[step][0]}`} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }} data-testid={`tgcf-question-${step}`}>
    <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-amber-200">Question {step + 1} of {questions.length}</p>
    <div className="mt-3 h-1 rounded-full bg-purple-200/15 overflow-hidden">
      <div className="h-full bg-red-300 transition-[width] duration-500" style={{ width: `${(step / questions.length) * 100}%` }} />
    </div>
    <p className="mt-4 font-display text-3xl md:text-4xl leading-tight" data-testid="tgcf-question-text">{questions[step][0]}</p>
    <div className="mt-6 grid gap-3">
      {questions[step][1].map(([a], i) => (
        <button key={a} onClick={() => onPick(i)} data-testid={`tgcf-answer-${step}-${i}`}
          className="text-left rounded-2xl border border-purple-200/25 bg-[#0e0616]/50 px-5 py-4 hover:border-red-300 hover:bg-red-900/20 transition-colors">
          {a}
        </button>
      ))}
    </div>
  </motion.div>
);

const Result = ({ r, onRestart }) => {
  const make = useMakeBracelet();
  const showCharm = useCharm(TGCF_CHARM);
  const makeIt = () => { make(r.beads, r.image); showCharm(); };
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
      className="grid sm:grid-cols-[180px_1fr] gap-6 items-center" data-testid="tgcf-result">
      <BraceletPreview beads={r.beads} className="max-w-[180px] mx-auto" />
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-amber-200">You are</p>
        <p className="mt-2 font-display text-4xl md:text-5xl" data-testid="tgcf-result-name">{r.name}</p>
        <p className="italic font-display text-xl text-red-200 mt-1">{r.title}</p>
        <p className="mt-4 text-purple-50/90 leading-relaxed">{r.text}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={makeIt} data-testid="tgcf-make-bracelet-btn"
            className="inline-flex items-center gap-2 rounded-full bg-purple-200 text-[#0e0616] px-6 py-3 font-medium hover:bg-amber-200 transition-colors">
            <Wand2 className="w-4 h-4" /> Make their bracelet
          </button>
          <button onClick={onRestart} data-testid="tgcf-restart-btn"
            className="inline-flex items-center gap-2 rounded-full border border-purple-200/40 px-6 py-3 hover:border-amber-200 hover:text-amber-200 transition-colors">
            <RotateCcw className="w-4 h-4" /> Try again
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const TgcfQuiz = () => {
  const [questions, setQuestions] = useState(pickQuestions);
  const [answers, setAnswers] = useState([]);
  const done = answers.length === questions.length;
  const winner = useMemo(
    () => (done ? matchCharacter(answers.map((a, q) => questions[q][1][a][1])) : null),
    [done, answers, questions],
  );
  const restart = () => { setQuestions(pickQuestions()); setAnswers([]); };

  return (
    <section id="tgcf" className="relative py-24 md:py-32 overflow-hidden" data-testid="tgcf-section">
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(60% 50% at 80% 40%, rgba(193,18,31,0.18), transparent 70%)" }} />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-red-200 mb-6">TGCF · fan corner</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.92] tracking-tight">
            <RevealLines inView lines={[<>Which TGCF</>, <em className="text-red-300">soul are you?</em>]} />
          </h2>
          <p className="mt-6 max-w-md text-purple-50/90 leading-relaxed">
            Answer seven questions, picked fresh from {BANK_SIZE} every time, to find your Heaven Official's Blessing match among {CHARACTERS.length} characters. Then turn their colors into a bracelet.
          </p>
          <p className="mt-4 text-xs text-purple-200/80">A fan-made quiz, not affiliated with the official series.</p>
        </div>
        <div className="lg:col-span-7 rounded-[28px] border border-red-200/20 bg-[#170a24]/80 backdrop-blur-xl p-6 md:p-10 min-h-[420px]">
          {done ? <Result r={winner} onRestart={restart} /> : <Question questions={questions} step={answers.length} onPick={(i) => setAnswers((a) => [...a, i])} />}
        </div>
      </div>
      <CharacterGallery charm={TGCF_CHARM} />
    </section>
  );
};
